import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import {
  agentMemoryAccessApi,
  type AgentMemoryAccessState,
} from "@/api/agent-memory-access";
import { queryKeys } from "@/lib/query-keys";
import {
  Brain,
  Eye,
  Share2,
  Trash2,
  Plus,
  Search,
  X,
  ShieldCheck,
  Lock,
  Power,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  type LucideIcon,
} from "lucide-react";

interface Props {
  agentId: string;
}

export function AgentMemoryAccessTab({ agentId }: Props) {
  const { tenantId, isAdmin } = useTenant();
  const queryClient = useQueryClient();
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState("");

  const { data: state, isLoading } = useQuery({
    queryKey: queryKeys.agentMemoryAccess.byAgent(agentId),
    queryFn: () => agentMemoryAccessApi.get(tenantId, agentId),
  });

  const { data: allAgents = [] } = useQuery({
    queryKey: queryKeys.agents.byCompany(tenantId),
    queryFn: async () => {
      const { agentsApi } = await import("@/api/agents");
      return agentsApi.listByCompany(tenantId);
    },
    enabled: !!tenantId,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.agentMemoryAccess.byAgent(agentId),
    });

  const updateMutation = useMutation({
    mutationFn: (data: {
      enabled?: boolean;
      memoryAdminRead?: boolean;
      memoryShared?: boolean;
    }) => agentMemoryAccessApi.update(tenantId, agentId, data),
    onSuccess: invalidate,
  });

  const grantMutation = useMutation({
    mutationFn: (readerAgentId: string) =>
      agentMemoryAccessApi.grantShare(tenantId, agentId, readerAgentId),
    onSuccess: () => {
      invalidate();
      setShowPicker(false);
      setSearch("");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (readerAgentId: string) =>
      agentMemoryAccessApi.revokeShare(tenantId, agentId, readerAgentId),
    onSuccess: invalidate,
  });

  const current: AgentMemoryAccessState = state ?? {
    enabled: false,
    memoryAdminRead: false,
    memoryShared: false,
    shares: [],
  };

  const alreadyShared = useMemo(
    () => new Set(current.shares.map((s) => s.readerAgentId)),
    [current.shares],
  );

  const pickableAgents = allAgents
    .filter((a) => a.id !== agentId && !alreadyShared.has(a.id))
    .filter(
      (a) =>
        !search ||
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        (a.role ?? "").toLowerCase().includes(search.toLowerCase()),
    );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <p className="text-xs text-text-muted">Loading memory access…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Brain size={18} />
            Memory Access
          </CardTitle>
          <p className="mt-1 max-w-2xl text-xs text-text-muted">
            Controls who can read this agent's long-term memories. Private by
            default; grant specific agents access below.
          </p>
        </CardHeader>
        {/* Three settings, three identical rows. Each used to carry a section
            heading that repeated the toggle's own label a line below it, with a
            divider between — a title, a heading and a label all saying the same
            thing. The heading and the divider went; a short name and one line
            of explanation stayed. Each is now an outlined box, the same one a
            skill or an MCP row uses on this tab. */}
        <CardContent className="space-y-3">
          {/* Memory MCP: gates the whole memory tool for this agent */}
          <section className="space-y-2">
          <ToggleRow
            icon={Power}
            label="Memory MCP"
            helper="Mounts the memory tools (remember / recall) on this agent and restarts its runtime. Off means the agent has no memory tools, so the two settings below do nothing."
            checked={current.enabled}
            disabled={!isAdmin || updateMutation.isPending}
            onChange={(v) => updateMutation.mutate({ enabled: v })}
          />
          {updateMutation.isPending && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-surface p-2 text-xs text-text-muted">
              <Loader2 size={12} className="animate-spin" />
              Syncing config and restarting the agent runtime — usually 5–10 s.
            </div>
          )}
          {updateMutation.data?.sync && updateMutation.data.sync.synced && updateMutation.data.sync.restarted && (
            <div className="flex items-start gap-2 rounded-md border border-success/30 bg-success/5 p-2 text-xs text-success">
              <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
              <span>
                Memory MCP {updateMutation.data.enabled ? "enabled" : "disabled"} — config pushed and agent runtime restarted.
              </span>
            </div>
          )}
          {updateMutation.data?.sync?.error && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-2 text-xs text-warning">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                Saved the flag, but couldn't fully apply it on the agent VM:
                {" "}<code className="text-[11px]">{updateMutation.data.sync.error}</code>
                {". "}The agent may need a manual <code className="text-[11px]">systemctl restart anthropic-agent</code>.
              </span>
            </div>
          )}
          </section>

          {/* Oversight: this agent reads everyone else's memory */}
          <section className="space-y-2">
          <ToggleRow
            icon={Eye}
            label="Oversight access"
            helper="Lets this agent recall memories from every agent in the tenant. For oversight roles like CEO, Compliance or Security; its own memories stay under the normal rules."
            checked={current.memoryAdminRead}
            disabled={!isAdmin || updateMutation.isPending}
            onChange={(v) => updateMutation.mutate({ memoryAdminRead: v })}
          />
          {current.memoryAdminRead && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-2 text-xs text-warning">
              <ShieldCheck size={14} className="mt-0.5 shrink-0" />
              <span>
                This agent currently sees every other agent's memories.
                Anything it says in chat could reference cross-agent context —
                use for supervisor personas only.
              </span>
            </div>
          )}
          </section>

          {/* Sharing toggle + share list */}
          <section className="space-y-2">
          <ToggleRow
            icon={Share2}
            label="Memory sharing"
            helper="Off keeps this agent's memories private. On lets only the agents you list below read them."
            checked={current.memoryShared}
            disabled={!isAdmin || updateMutation.isPending}
            onChange={(v) => updateMutation.mutate({ memoryShared: v })}
          />

          {!current.memoryShared ? (
            <div className="flex items-start gap-2 rounded-md border border-border bg-surface p-3 text-xs text-text-muted">
              <Lock size={14} className="mt-0.5 shrink-0" />
              <span>
                Sharing is off — this agent's memories are fully private. Turn
                the toggle on to grant read access to specific agents.
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-text-muted">
                  {current.shares.length === 0
                    ? "No agents have been granted read access yet."
                    : `${current.shares.length} agent${current.shares.length === 1 ? "" : "s"} granted read access.`}
                </p>
                {isAdmin && !showPicker && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setShowPicker(true);
                      setSearch("");
                    }}
                  >
                    <Plus size={14} className="mr-1.5" />
                    Add reader
                  </Button>
                )}
              </div>

              {showPicker && (
                <Card className="border-accent/40">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between text-sm">
                      <span>Select an agent to grant read access</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setShowPicker(false);
                          setSearch("");
                        }}
                      >
                        <X size={14} />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="relative mb-3">
                      <Search
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                      />
                      <input
                        type="text"
                        placeholder="Search agents…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full rounded-md border border-border bg-surface pl-8 pr-3 py-2 text-sm"
                        autoFocus
                      />
                    </div>
                    <div className="max-h-[260px] overflow-y-auto rounded-md border border-border">
                      {pickableAgents.length === 0 ? (
                        <p className="py-6 text-xs text-text-muted text-center">
                          {allAgents.length <= 1
                            ? "No other agents to share with yet."
                            : "All eligible agents already have access, or no matches."}
                        </p>
                      ) : (
                        pickableAgents.map((agent) => (
                          <button
                            key={agent.id}
                            className="flex w-full items-center gap-3 border-b border-border last:border-0 px-3 py-2.5 text-left hover:bg-surface-hover text-sm transition-colors"
                            onClick={() => grantMutation.mutate(agent.id)}
                            disabled={grantMutation.isPending}
                          >
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 shrink-0">
                              <Brain size={14} className="text-accent" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-text-primary truncate">
                                {agent.name}
                              </p>
                              <p className="text-xs text-text-muted truncate">
                                {agent.role ?? "—"}
                              </p>
                            </div>
                            <Plus size={16} className="text-accent shrink-0" />
                          </button>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {current.shares.length > 0 && (
                <div className="flex flex-col gap-1">
                  {current.shares.map((share) => (
                    <div
                      key={share.readerAgentId}
                      className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-surface-hover"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10">
                          <Brain size={14} className="text-accent" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {share.readerName}
                          </p>
                          <p className="text-xs text-text-muted">
                            {share.readerRole ?? "—"} · granted{" "}
                            {new Date(share.grantedAt).toLocaleDateString()}
                            {share.grantedByName || share.grantedByEmail
                              ? ` by ${share.grantedByName ?? share.grantedByEmail}`
                              : ""}
                          </p>
                        </div>
                      </div>
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            revokeMutation.mutate(share.readerAgentId)
                          }
                          disabled={revokeMutation.isPending}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </section>
        </CardContent>
      </Card>

      {!isAdmin && (
        <p className="text-xs text-text-muted">
          Only tenant admins can change these settings.
        </p>
      )}
    </div>
  );
}

interface ToggleRowProps {
  icon: LucideIcon;
  label: string;
  helper?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}

function ToggleRow({
  icon: Icon,
  label,
  helper,
  checked,
  disabled,
  onChange,
}: ToggleRowProps) {
  // Same box as a skill or an MCP row on the Config tab: outlined, not filled,
  // px-3 py-2.5, a 14px accent icon, then the name. The description sits under
  // it, indented to the name rather than the icon.
  return (
    <label
      className={`block rounded border border-border px-3 py-2.5 ${
        disabled ? "opacity-60" : "cursor-pointer"
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-2">
          <Icon size={14} className="shrink-0 text-accent" />
          <span className="text-sm font-medium text-text-primary">{label}</span>
        </div>
        <input
          type="checkbox"
          className="h-4 w-4 shrink-0 accent-accent"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </div>
      {helper && <p className="mt-1 pl-[22px] text-xs text-text-muted">{helper}</p>}
    </label>
  );
}
